import { Stack, Typography } from '@mui/material';
import { AWTypes } from 'enums';
import { Scores } from 'features/augmented-writing/augmented-writing.types';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { Assessment } from 'types/analysis-result.types';
import AnalysisResultLegend from './analysis-result-legend';
import MatchCompareGraph from './match-compare-graph';
import MotiveChart from './motive-chart';
import UserPersona from './user-persona';

interface Props {
  scores: Scores | undefined;
  userAssessment?: Assessment | undefined;
  clickedChartCategoryKey?: string | undefined;
  userDone?: boolean;
  domain?: AWTypes;
  /** 'default' keeps the legacy column, 'panel' renders the two-zone panel design */
  variant?: 'default' | 'panel';

  onChartCategoryClicked?(category: any): void;
}

const AnalysisDetails: React.FC<Props> = ({
  scores,
  userAssessment,
  clickedChartCategoryKey,
  userDone,
  domain,
  variant = 'default',
  onChartCategoryClicked,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const isPanel = variant === 'panel';
  const contactProfile = scores?.percentiles.profile;
  const userProfile = scores?.percentiles.text;

  return (
    <Stack direction={'column'} gap={isPanel ? '16px' : '8px'}>
      <AnalysisResultLegend variant={variant} disableBorder={isPanel} />
      <div className={cx(classes.card, isPanel ? classes.panelCard : null)}>
        <Typography className={cx(classes.title, isPanel ? classes.panelTitle : null)}>{t('motiveMatch')}</Typography>
        <MotiveChart
          contactScores={contactProfile}
          userScores={userProfile}
          clickedChartCategoryKey={clickedChartCategoryKey}
          onChartCategoryClicked={onChartCategoryClicked}
        />
      </div>
      <MatchCompareGraph
        title={t('languageStyleMatch')}
        categoryKey={'authenticity'}
        leftText={t('formal')}
        rightText={t('informal')}
        contactScore={contactProfile?.authenticity}
        userScore={userProfile?.authenticity}
        clickedChartCategoryKey={clickedChartCategoryKey}
        variant={variant}
        disableBorder={isPanel}
        maxWidth={isPanel ? '100%' : undefined}
        onChartCategoryClicked={onChartCategoryClicked}
      />
      {!_.isNil(scores?.diversity) || domain === AWTypes.TALENT ? (
        <MatchCompareGraph
          title={t('genderMatch')}
          categoryKey={'diversity'}
          leftText={t('masc')}
          rightText={t('fem')}
          userScore={scores?.diversity}
          contactScore={1}
          clickedChartCategoryKey={clickedChartCategoryKey}
          variant={variant}
          disableBorder={isPanel}
          maxWidth={isPanel ? '100%' : undefined}
          onChartCategoryClicked={onChartCategoryClicked}
        />
      ) : null}
      {!_.isNil(userAssessment) && !userDone ? <UserPersona userAssessment={userAssessment} /> : null}
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  title: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    textAlign: 'center',
    whiteSpace: 'pre-wrap',
    marginBottom: '8px',
  },
  panelTitle: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 500,
    textTransform: 'none',
    textAlign: 'start',
  },
  card: {
    width: '100%',
    background: '#FFF',
    border: `2px solid ${theme.palette.backgrounds.main}`,
    padding: '8px',
  },
  panelCard: {
    border: 'unset',
    padding: 0,
  },
}));

export default AnalysisDetails;
